import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { IconSolidInfo } from '@notino/react-styleguide';
import { VoucherVariantFeatureFlag } from '@notino/shared/definitions/types';

import { PortalTooltip } from '@components/PortalTooltip';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { ICurrency } from '@containers/App/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import messages from '../../../../../containers/ProductDetailContainer/messages';
import { usePriceLogic } from '../../hooks/usePriceLogic';
import { useProductDetailContext } from '../../ProductDetailContext';
import { Line } from '../SelectedVariant/styled';

import {
  ConditionalVoucherDiscount,
  IConditionalVoucherDiscount,
} from './ConditionalVoucherDiscount';
import { VoucherDiscountContext } from './context';
import { MultiTierVoucherDiscount } from './MultiTierVoucherDiscount';
import { LineWrapper, MinPriceWrapper } from './styled';
import { isConditionalDiscount } from './utils';
import { IVoucherDiscount, VoucherDiscount } from './VoucherDiscount';

interface IVoucherDiscountProps {
  voucherDiscount: IVoucherDiscount | IConditionalVoucherDiscount;
  currency: ICurrency;
  isUnderSelectBox?: boolean;
  hideLine?: boolean;
  isArrowHidden?: boolean;
}

export const VoucherDiscountMaster = ({
  voucherDiscount,
  hideLine,
  ...rest
}: IVoucherDiscountProps) => {
  const { voucher_variant, experiment_pd_min_price_under_voucher } =
    useFeatureFlags();
  const { currentVariant } = useProductDetailContext();
  const { bottomRecentPrice } = usePriceLogic(currentVariant);
  const formatPrice = useFormatPrice();

  const getComponent = () => {
    if (isConditionalDiscount(voucherDiscount)) {
      return voucherDiscount.discountConditions.length > 1 ? (
        <MultiTierVoucherDiscount voucherDiscount={voucherDiscount} />
      ) : (
        <ConditionalVoucherDiscount voucherDiscount={voucherDiscount} />
      );
    }
    return <VoucherDiscount voucherDiscount={voucherDiscount} />;
  };

  const hasContrastBg =
    voucher_variant === VoucherVariantFeatureFlag.ContrastBackgroundWithLabel ||
    voucher_variant === VoucherVariantFeatureFlag.ContrastBackground;

  const hasBlackFridayDiscount =
    currentVariant.attributes?.VoucherDiscount?.tags?.includes(
      'summerblackfriday'
    ) ||
    currentVariant.attributes?.VoucherDiscount?.tags?.includes('blackfriday');

  return (
    <VoucherDiscountContext.Provider
      value={{ ...rest, hasContrastBg, hasBlackFridayDiscount }}
    >
      {getComponent()}

      {experiment_pd_min_price_under_voucher && bottomRecentPrice && (
        <MinPriceWrapper>
          <FormattedMessage {...messages.minimumRecentPrice} />{' '}
          {formatPrice(bottomRecentPrice.value)?.formattedPriceWithCurrency}
          <PortalTooltip
            interactive={true}
            content={
              <PortalTooltip.Content>
                {bottomRecentPrice.tooltip}
              </PortalTooltip.Content>
            }
          >
            <IconSolidInfo color="icon.disabled" height="1rem" />
          </PortalTooltip>
        </MinPriceWrapper>
      )}

      {!hideLine && (
        <LineWrapper>
          <Line />
        </LineWrapper>
      )}
    </VoucherDiscountContext.Provider>
  );
};
